import { format } from 'date-fns'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PhotoModerationEntry, PhotoModerationStatus } from '@/api/types'
import { Badge } from '@/components/Badge/Badge'
import { CompactTable } from '@/components/Table'
import { Heading } from '@/components/Typography'
import PhotoModerationDialog from './PhotoModerationDialog'
import usePhotosModeration from './usePhotosModeration'

type Props = {
  entries: PhotoModerationEntry[]
}

export default function PhotosModeration({ entries }: Props) {
  const { t } = useTranslation()
  const [selectedEntry, setSelectedEntry] = useState<PhotoModerationEntry | null>(null)
  const { approvePhoto, rejectPhoto } = usePhotosModeration()

  const tableColumns = [
    { label: 'ID', key: 'id', width: 80 },
    { label: 'Recipe Name', key: 'recipeName' },
    { label: 'State', key: 'state', width: 120 },
    {
      label: 'Created By',
      key: 'createdBy',
      width: 150,
    },
    {
      label: 'Creation Date',
      key: 'creationDate',
      width: 150,
      format: (date: Date) => format(date, 'dd MMM, yyyy'),
    },
  ]

  const handleRowAction = (key: number) => {
    const entry = entries.find((entry) => entry.id === key)

    if (entry) {
      setSelectedEntry(entry)
    }
  }

  return (
    <>
      <Heading as="h3">{t('photos.needsReview')}</Heading>
      <CompactTable
        aria-label="Moderation events"
        columns={tableColumns}
        rows={entries.map((event) => ({
          ...event,
          state: (
            <Badge type={getStateColor(event.moderationStatus)}>
              {t(('photos.moderationState.' + event.moderationStatus) as any)}
            </Badge>
          ),
        }))}
        onRowAction={handleRowAction}
        search={false}
      />

      <PhotoModerationDialog
        id={selectedEntry?.id}
        isOpen={!!selectedEntry?.id}
        onOpenChange={() => setSelectedEntry(null)}
        onApprove={approvePhoto}
        onReject={rejectPhoto}
      />
    </>
  )
}

const getStateColor = (status: PhotoModerationStatus) => {
  switch (status) {
    case 'UNDER_REVIEW':
      return 'neutral'
    case 'APPROVED':
      return 'success'
    case 'REJECTED':
      return 'error'
  }
}
